import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  ClipboardList, Plus, Calendar, Users, AlertTriangle, CheckCircle, 
  Clock, MapPin, Shield, Sun, CloudRain, Wind, Thermometer, 
  FileText, UserCheck, Wrench, Printer, Download, FileDown, Eye, QrCode, Sparkles
} from 'lucide-react';
import { QRShareButton } from './QRCodeShare';

const API = process.env.REACT_APP_BACKEND_URL;

// Generate printable HTML for PTP
const generatePrintableHTML = (ptp) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Pre-Task Plan - ${ptp.project_name}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          font-family: Arial, sans-serif; 
          padding: 20px; 
          max-width: 800px; 
          margin: 0 auto; 
          line-height: 1.4;
          font-size: 11pt;
        }
        .header { 
          text-align: center; 
          border-bottom: 3px solid #1e40af; 
          padding-bottom: 15px; 
          margin-bottom: 20px; 
        }
        .header h1 { color: #1e40af; font-size: 20pt; margin-bottom: 5px; }
        .header h2 { color: #374151; font-size: 14pt; font-weight: normal; }
        .info-grid { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 10px; 
          margin-bottom: 20px;
          background: #f8fafc;
          padding: 15px;
          border-radius: 8px;
        }
        .info-item { display: flex; }
        .info-label { font-weight: bold; min-width: 120px; color: #374151; }
        .section { margin-bottom: 20px; }
        .section-title { 
          font-size: 12pt; 
          font-weight: bold; 
          color: #1e40af; 
          border-bottom: 2px solid #e5e7eb; 
          padding-bottom: 5px; 
          margin-bottom: 10px; 
        }
        .task-item { 
          padding: 8px 12px; 
          background: #f1f5f9; 
          margin-bottom: 5px; 
          border-radius: 4px;
          border-left: 3px solid #3b82f6;
        }
        .hazard-card { 
          border: 1px solid #e5e7eb; 
          padding: 12px; 
          margin-bottom: 10px; 
          border-radius: 8px;
          page-break-inside: avoid;
        }
        .hazard-critical { border-left: 4px solid #ef4444; background: #fef2f2; }
        .hazard-high { border-left: 4px solid #f97316; background: #fff7ed; }
        .hazard-medium { border-left: 4px solid #eab308; background: #fefce8; }
        .hazard-low { border-left: 4px solid #22c55e; background: #f0fdf4; }
        .hazard-title { font-weight: bold; margin-bottom: 5px; }
        .hazard-controls { font-size: 10pt; color: #4b5563; }
        .crew-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .crew-table th, .crew-table td { 
          border: 1px solid #d1d5db; 
          padding: 10px; 
          text-align: left; 
        }
        .crew-table th { background: #f3f4f6; font-weight: bold; }
        .signature-cell { height: 40px; }
        .status-badge { 
          display: inline-block; 
          padding: 4px 12px; 
          border-radius: 12px; 
          font-size: 10pt;
          font-weight: bold;
        }
        .status-signed { background: #dcfce7; color: #166534; }
        .status-pending { background: #fef3c7; color: #92400e; }
        .emergency-box { 
          background: #fef2f2; 
          border: 2px solid #ef4444; 
          padding: 15px; 
          border-radius: 8px;
          margin-top: 20px;
        }
        .emergency-title { color: #ef4444; font-weight: bold; margin-bottom: 10px; }
        .footer { 
          margin-top: 30px; 
          padding-top: 15px; 
          border-top: 2px solid #e5e7eb; 
          text-align: center;
          font-size: 9pt;
          color: #6b7280;
        }
        .signature-line { 
          border-bottom: 1px solid #000; 
          width: 200px; 
          display: inline-block; 
          margin: 0 10px; 
        }
        @media print {
          body { padding: 10px; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🛡️ PRE-TASK PLAN (PTP)</h1>
        <h2>${ptp.project_name}</h2>
      </div>

      <div class="info-grid">
        <div class="info-item"><span class="info-label">Date:</span> ${ptp.date}</div>
        <div class="info-item"><span class="info-label">Shift:</span> ${ptp.shift?.charAt(0).toUpperCase() + ptp.shift?.slice(1)} Shift</div>
        <div class="info-item"><span class="info-label">Location:</span> ${ptp.location}</div>
        <div class="info-item"><span class="info-label">Supervisor:</span> ${ptp.supervisor}</div>
        <div class="info-item"><span class="info-label">Status:</span> <span class="status-badge ${ptp.status === 'completed' ? 'status-signed' : 'status-pending'}">${ptp.status?.toUpperCase()}</span></div>
        <div class="info-item"><span class="info-label">PTP ID:</span> ${ptp.id?.slice(0, 8)}</div>
      </div>

      ${ptp.weather_conditions?.length > 0 ? `
      <div class="section">
        <div class="section-title">⛅ Weather Conditions</div>
        <div>${ptp.weather_conditions.join(', ')}</div>
      </div>
      ` : ''}

      <div class="section">
        <div class="section-title">📋 Tasks Planned (${ptp.tasks_planned?.length || 0})</div>
        ${ptp.tasks_planned?.map((task, i) => `
          <div class="task-item">${i + 1}. ${task}</div>
        `).join('') || '<p>No tasks specified</p>'}
      </div>

      <div class="section">
        <div class="section-title">⚠️ Identified Hazards & Controls (${ptp.hazards?.length || 0})</div>
        ${ptp.hazards?.map(hazard => `
          <div class="hazard-card hazard-${hazard.level || 'medium'}">
            <div class="hazard-title">${hazard.hazard}</div>
            <div class="hazard-controls"><strong>Controls:</strong> ${hazard.controls?.join(', ') || 'None specified'}</div>
            <div class="hazard-controls"><strong>PPE Required:</strong> ${hazard.ppe_required?.join(', ') || 'None specified'}</div>
            <div class="hazard-controls"><strong>Risk Level:</strong> ${hazard.level?.toUpperCase() || 'MEDIUM'}</div>
          </div>
        `).join('') || '<p>No hazards identified</p>'}
      </div>

      <div class="section">
        <div class="section-title">👷 Crew Sign-Off Sheet</div>
        <table class="crew-table">
          <thead>
            <tr>
              <th style="width: 30%">Name</th>
              <th style="width: 20%">Role</th>
              <th style="width: 25%">Signature</th>
              <th style="width: 15%">Date/Time</th>
              <th style="width: 10%">Status</th>
            </tr>
          </thead>
          <tbody>
            ${ptp.crew?.map(member => `
              <tr>
                <td>${member.name}</td>
                <td>${member.role}</td>
                <td class="signature-cell">${member.signed ? '✓ Signed' : ''}</td>
                <td>${member.signed_at ? new Date(member.signed_at).toLocaleString() : ''}</td>
                <td><span class="status-badge ${member.signed ? 'status-signed' : 'status-pending'}">${member.signed ? 'SIGNED' : 'PENDING'}</span></td>
              </tr>
            `).join('') || `
              <tr><td colspan="5" style="text-align: center; color: #6b7280;">No crew members assigned</td></tr>
            `}
            ${/* Add empty rows for manual sign-off */ ''}
            <tr><td></td><td></td><td class="signature-cell"></td><td></td><td></td></tr>
            <tr><td></td><td></td><td class="signature-cell"></td><td></td><td></td></tr>
            <tr><td></td><td></td><td class="signature-cell"></td><td></td><td></td></tr>
          </tbody>
        </table>
      </div>

      ${ptp.emergency_contact || ptp.emergency_phone || ptp.nearest_hospital ? `
      <div class="emergency-box">
        <div class="emergency-title">🚨 EMERGENCY INFORMATION</div>
        ${ptp.emergency_contact ? `<p><strong>Emergency Contact:</strong> ${ptp.emergency_contact}</p>` : ''}
        ${ptp.emergency_phone ? `<p><strong>Emergency Phone:</strong> ${ptp.emergency_phone}</p>` : ''}
        ${ptp.nearest_hospital ? `<p><strong>Nearest Hospital:</strong> ${ptp.nearest_hospital}</p>` : ''}
      </div>
      ` : ''}

      <div class="footer">
        <p style="margin-bottom: 15px;">
          <strong>Supervisor Approval:</strong> <span class="signature-line"></span> 
          <strong>Date:</strong> <span class="signature-line"></span>
        </p>
        <p>Generated from WSafe AI Safety Platform</p>
        <p>© ${new Date().getFullYear()} WorkSafeAgent LLC | www.wsafeai.com</p>
      </div>
    </body>
    </html>
  `;
};

// Print PTP
const printPTP = (ptp) => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(generatePrintableHTML(ptp));
  printWindow.document.close();
  printWindow.print();
};

// Download as PDF (text-based)
const downloadPDF = (ptp) => {
  const content = `
PRE-TASK PLAN (PTP)
==================

Project: ${ptp.project_name}
Date: ${ptp.date}
Location: ${ptp.location}
Shift: ${ptp.shift}
Supervisor: ${ptp.supervisor}
Status: ${ptp.status?.toUpperCase()}

${ptp.weather_conditions?.length > 0 ? `
WEATHER CONDITIONS
------------------
${ptp.weather_conditions.join(', ')}
` : ''}

TASKS PLANNED
-------------
${ptp.tasks_planned?.map((task, i) => `${i + 1}. ${task}`).join('\n') || 'No tasks specified'}

IDENTIFIED HAZARDS & CONTROLS
-----------------------------
${ptp.hazards?.map((h, i) => `
${i + 1}. ${h.hazard}
   Risk Level: ${h.level?.toUpperCase() || 'MEDIUM'}
   Controls: ${h.controls?.join(', ') || 'None'}
   PPE Required: ${h.ppe_required?.join(', ') || 'None'}
`).join('\n') || 'No hazards identified'}

CREW SIGN-OFF
-------------
Name                Role              Signed    Date/Time
----                ----              ------    ---------
${ptp.crew?.map(m => `${m.name.padEnd(20)}${m.role.padEnd(18)}${m.signed ? 'YES' : 'NO'}       ${m.signed_at ? new Date(m.signed_at).toLocaleString() : ''}`).join('\n') || 'No crew assigned'}

EMERGENCY INFORMATION
---------------------
${ptp.emergency_contact ? `Contact: ${ptp.emergency_contact}` : ''}
${ptp.emergency_phone ? `Phone: ${ptp.emergency_phone}` : ''}
${ptp.nearest_hospital ? `Nearest Hospital: ${ptp.nearest_hospital}` : ''}

Supervisor Approval: _________________ Date: _________

Generated from WSafe AI Safety Platform
`;

  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PTP-${ptp.project_name?.replace(/[^a-z0-9]/gi, '-')}-${ptp.date}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Download as Word Doc (HTML format that Word can open)
const downloadWord = (ptp) => {
  const htmlContent = generatePrintableHTML(ptp);
  const blob = new Blob([htmlContent], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PTP-${ptp.project_name?.replace(/[^a-z0-9]/gi, '-')}-${ptp.date}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const DailyPTPPage = () => {
  const [ptps, setPtps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPTP, setSelectedPTP] = useState(null);
  const [viewMode, setViewMode] = useState('details'); // 'details' or 'document'
  const [hazardTemplates, setHazardTemplates] = useState({});
  const [stats, setStats] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    project_name: '',
    location: '',
    date: new Date().toISOString().split('T')[0],
    shift: 'day',
    supervisor: '',
    tasks_planned: [],
    hazards: [],
    crew: [],
    weather_conditions: [],
    special_permits_required: [],
    emergency_contact: '',
    emergency_phone: '',
    nearest_hospital: ''
  });

  const [newTask, setNewTask] = useState('');
  const [newCrewMember, setNewCrewMember] = useState({ name: '', role: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ptpsRes, templatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/daily-ptp`),
        axios.get(`${API}/api/daily-ptp/templates/hazards`),
        axios.get(`${API}/api/daily-ptp/stats/summary`)
      ]);
      setPtps(ptpsRes.data.ptps || []);
      setHazardTemplates(templatesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePTP = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/daily-ptp`, formData);
      toast({ title: 'Success', description: 'Pre-Task Plan created!' });
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create PTP', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      project_name: '',
      location: '',
      date: new Date().toISOString().split('T')[0],
      shift: 'day',
      supervisor: '',
      tasks_planned: [],
      hazards: [],
      crew: [],
      weather_conditions: [],
      special_permits_required: [],
      emergency_contact: '',
      emergency_phone: '',
      nearest_hospital: ''
    });
    setNewTask('');
    setNewCrewMember({ name: '', role: '' });
  };

  const addTask = () => {
    if (newTask.trim()) {
      setFormData({ ...formData, tasks_planned: [...formData.tasks_planned, newTask.trim()] });
      setNewTask('');
    }
  };

  const removeTask = (index) => {
    setFormData({ ...formData, tasks_planned: formData.tasks_planned.filter((_, i) => i !== index) });
  };

  const addCrewMember = () => {
    if (newCrewMember.name.trim() && newCrewMember.role.trim()) {
      setFormData({ ...formData, crew: [...formData.crew, { ...newCrewMember, signed: false }] });
      setNewCrewMember({ name: '', role: '' });
    }
  };

  const removeCrewMember = (index) => {
    setFormData({ ...formData, crew: formData.crew.filter((_, i) => i !== index) });
  };

  const addHazardFromTemplate = (hazardKey, hazardType) => {
    const template = hazardTemplates[hazardType]?.[hazardKey];
    if (template) {
      const hazard = {
        hazard: template.hazard,
        controls: template.controls,
        ppe_required: template.ppe_required,
        level: template.level
      };
      setFormData({ ...formData, hazards: [...formData.hazards, hazard] });
    }
  };

  const signPTP = async (ptpId, crewName) => {
    try {
      await axios.post(`${API}/api/daily-ptp/${ptpId}/sign?crew_name=${encodeURIComponent(crewName)}`);
      toast({ title: 'Signed!', description: `${crewName} has signed the PTP` });
      fetchData();
      if (selectedPTP?.id === ptpId) {
        const res = await axios.get(`${API}/api/daily-ptp/${ptpId}`);
        setSelectedPTP(res.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to sign', variant: 'destructive' });
    }
  };

  const completePTP = async (ptpId) => {
    try {
      await axios.post(`${API}/api/daily-ptp/${ptpId}/complete`);
      toast({ title: 'Completed!', description: 'PTP marked as completed' });
      fetchData();
      setSelectedPTP(null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to complete PTP', variant: 'destructive' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHazardLevelColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const weatherIcons = {
    clear: <Sun className="w-4 h-4" />,
    hot: <Thermometer className="w-4 h-4" />,
    cold: <Thermometer className="w-4 h-4" />,
    rain: <CloudRain className="w-4 h-4" />,
    wind: <Wind className="w-4 h-4" />,
    storm: <CloudRain className="w-4 h-4" />
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daily Pre-Task Plans (PTP)</h1>
          <p className="text-gray-600">Job Hazard Analysis - Start every shift safely</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/ai-safety-generator'}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Generate
          </Button>
          <Button onClick={() => setShowCreateForm(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            New PTP
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total PTPs</p>
                <p className="text-2xl font-bold">{stats?.total_ptps || 0}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Today</p>
                <p className="text-2xl font-bold text-green-600">{stats?.active || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.completed || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completion Rate</p>
                <p className="text-2xl font-bold text-purple-600">{stats?.completion_rate || 0}%</p>
              </div>
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PTP List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {ptps.map((ptp) => (
          <Card key={ptp.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => { setSelectedPTP(ptp); setViewMode('details'); }}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <ClipboardList className="w-5 h-5 mr-2 text-blue-600" />
                  {ptp.project_name}
                </CardTitle>
                <Badge className={getStatusColor(ptp.status)}>{ptp.status}</Badge>
              </div>
              <CardDescription className="flex items-center gap-4">
                <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" />{ptp.location}</span>
                <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" />{ptp.date}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Supervisor:</span>
                  <span className="font-medium">{ptp.supervisor}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Shift:</span>
                  <Badge variant="outline">{ptp.shift}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Crew Signed:</span>
                  <span className={`font-medium ${ptp.all_signed ? 'text-green-600' : 'text-orange-600'}`}>
                    {ptp.sign_count || 0} / {ptp.total_crew || 0}
                  </span>
                </div>
                {ptp.hazards?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <span className="text-sm text-gray-600">{ptp.hazards.length} Hazards Identified</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {ptps.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pre-Task Plans</h3>
            <p className="text-gray-600 mb-4">Create your first PTP to start the shift safely</p>
            <Button onClick={() => setShowCreateForm(true)}>Create PTP</Button>
          </CardContent>
        </Card>
      )}

      {/* Create PTP Dialog */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Pre-Task Plan</DialogTitle>
            <DialogDescription>Document hazards and controls before starting work</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePTP} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="tasks">Tasks</TabsTrigger>
                <TabsTrigger value="hazards">Hazards</TabsTrigger>
                <TabsTrigger value="crew">Crew</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Project Name *</Label>
                    <Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Location *</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Date *</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Shift *</Label>
                    <Select value={formData.shift} onValueChange={(v) => setFormData({...formData, shift: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Day Shift</SelectItem>
                        <SelectItem value="night">Night Shift</SelectItem>
                        <SelectItem value="swing">Swing Shift</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Supervisor *</Label>
                    <Input value={formData.supervisor} onChange={(e) => setFormData({...formData, supervisor: e.target.value})} required />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Emergency Contact</Label>
                    <Input value={formData.emergency_contact} onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})} />
                  </div>
                  <div>
                    <Label>Emergency Phone</Label>
                    <Input value={formData.emergency_phone} onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})} />
                  </div>
                  <div>
                    <Label>Nearest Hospital</Label>
                    <Input value={formData.nearest_hospital} onChange={(e) => setFormData({...formData, nearest_hospital: e.target.value})} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tasks" className="space-y-4 pt-4">
                <div className="flex gap-2">
                  <Input placeholder="Enter task description" value={newTask} onChange={(e) => setNewTask(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTask())} />
                  <Button type="button" onClick={addTask}>Add Task</Button>
                </div>
                <div className="space-y-2">
                  {formData.tasks_planned.map((task, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="flex items-center"><Wrench className="w-4 h-4 mr-2 text-blue-600" />{task}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeTask(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="hazards" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Construction Hazards</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {hazardTemplates.construction && Object.keys(hazardTemplates.construction).map((key) => (
                        <Button key={key} type="button" variant="outline" size="sm" className="w-full justify-start text-xs" onClick={() => addHazardFromTemplate(key, 'construction')}>
                          <AlertTriangle className="w-3 h-3 mr-2" />{hazardTemplates.construction[key].hazard}
                        </Button>
                      ))}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Data Center Hazards</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {hazardTemplates.data_center && Object.keys(hazardTemplates.data_center).map((key) => (
                        <Button key={key} type="button" variant="outline" size="sm" className="w-full justify-start text-xs" onClick={() => addHazardFromTemplate(key, 'data_center')}>
                          <AlertTriangle className="w-3 h-3 mr-2" />{hazardTemplates.data_center[key].hazard}
                        </Button>
                      ))}
                    </CardContent>
                  </Card>
                </div>
                <div className="space-y-2">
                  <Label>Selected Hazards ({formData.hazards.length})</Label>
                  {formData.hazards.map((hazard, index) => (
                    <Card key={index} className={`border-l-4 ${getHazardLevelColor(hazard.level)}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{hazard.hazard}</p>
                            <p className="text-xs text-gray-600 mt-1">Controls: {hazard.controls?.join(', ')}</p>
                            <p className="text-xs text-gray-600">PPE: {hazard.ppe_required?.join(', ')}</p>
                          </div>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({...formData, hazards: formData.hazards.filter((_, i) => i !== index)})}>Remove</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="crew" className="space-y-4 pt-4">
                <div className="flex gap-2">
                  <Input placeholder="Crew member name" value={newCrewMember.name} onChange={(e) => setNewCrewMember({...newCrewMember, name: e.target.value})} />
                  <Input placeholder="Role" value={newCrewMember.role} onChange={(e) => setNewCrewMember({...newCrewMember, role: e.target.value})} />
                  <Button type="button" onClick={addCrewMember}>Add</Button>
                </div>
                <div className="space-y-2">
                  {formData.crew.map((member, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="flex items-center"><Users className="w-4 h-4 mr-2 text-blue-600" />{member.name} - {member.role}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCrewMember(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create PTP</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* PTP Detail/Document Dialog */}
      <Dialog open={!!selectedPTP} onOpenChange={() => setSelectedPTP(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedPTP && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    {selectedPTP.project_name}
                  </DialogTitle>
                  <Badge className={getStatusColor(selectedPTP.status)}>{selectedPTP.status}</Badge>
                </div>
                <DialogDescription>{selectedPTP.location} • {selectedPTP.date} • {selectedPTP.shift} shift</DialogDescription>
              </DialogHeader>
              
              {/* View Mode Toggle & Export Buttons */}
              <div className="flex items-center justify-between border-b pb-4">
                <div className="flex gap-2">
                  <Button 
                    variant={viewMode === 'details' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('details')}
                  >
                    <Eye className="w-4 h-4 mr-2" /> Details View
                  </Button>
                  <Button 
                    variant={viewMode === 'document' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('document')}
                  >
                    <FileText className="w-4 h-4 mr-2" /> Document View
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => printPTP(selectedPTP)}>
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadPDF(selectedPTP)}>
                    <Download className="w-4 h-4 mr-2" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadWord(selectedPTP)}>
                    <FileDown className="w-4 h-4 mr-2" /> Word
                  </Button>
                  <QRShareButton
                    documentType="ptp"
                    documentId={selectedPTP?.id}
                    documentTitle={`PTP - ${selectedPTP?.supervisor_name || 'Unknown'} - ${new Date(selectedPTP?.date).toLocaleDateString()}`}
                    variant="outline"
                    size="sm"
                  />
                </div>
              </div>

              {viewMode === 'details' ? (
                /* Details View - Interactive */
                <div className="space-y-4">
                  {/* Supervisor Info */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <UserCheck className="w-4 h-4" /> Supervisor Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-gray-500">Supervisor:</span> <strong>{selectedPTP.supervisor}</strong></div>
                        <div><span className="text-gray-500">Shift:</span> <strong>{selectedPTP.shift}</strong></div>
                        {selectedPTP.emergency_contact && <div><span className="text-gray-500">Emergency Contact:</span> <strong>{selectedPTP.emergency_contact}</strong></div>}
                        {selectedPTP.emergency_phone && <div><span className="text-gray-500">Emergency Phone:</span> <strong>{selectedPTP.emergency_phone}</strong></div>}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tasks */}
                  {selectedPTP.tasks_planned?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wrench className="w-4 h-4" /> Tasks Planned ({selectedPTP.tasks_planned.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedPTP.tasks_planned.map((task, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                              <span className="text-blue-600 font-medium">{index + 1}.</span>
                              {task}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Hazards */}
                  {selectedPTP.hazards?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500" /> Identified Hazards ({selectedPTP.hazards.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {selectedPTP.hazards.map((hazard, index) => (
                          <div key={index} className={`p-3 rounded-lg border-l-4 ${getHazardLevelColor(hazard.level)}`}>
                            <p className="font-medium">{hazard.hazard}</p>
                            <p className="text-xs text-gray-600 mt-1"><strong>Controls:</strong> {hazard.controls?.join(', ')}</p>
                            <p className="text-xs text-gray-600"><strong>PPE:</strong> {hazard.ppe_required?.join(', ')}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {/* Crew Sign-Off */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Users className="w-4 h-4" /> Crew Sign-Off ({selectedPTP.sign_count || 0}/{selectedPTP.crew?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {selectedPTP.crew?.map((member, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="flex items-center">
                              {member.signed ? <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> : <Clock className="w-4 h-4 mr-2 text-orange-500" />}
                              {member.name} - {member.role}
                            </span>
                            {!member.signed && selectedPTP.status !== 'completed' && (
                              <Button size="sm" onClick={() => signPTP(selectedPTP.id, member.name)}>Sign</Button>
                            )}
                            {member.signed && <Badge className="bg-green-100 text-green-800">Signed {member.signed_at && `at ${new Date(member.signed_at).toLocaleTimeString()}`}</Badge>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {selectedPTP.status === 'active' && (
                    <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => completePTP(selectedPTP.id)}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Complete PTP
                    </Button>
                  )}
                </div>
              ) : (
                /* Document View - Full Printable Document */
                <div className="border rounded-lg p-6 bg-white">
                  <div className="text-center border-b-2 border-blue-600 pb-4 mb-6">
                    <h2 className="text-2xl font-bold text-blue-600">🛡️ PRE-TASK PLAN (PTP)</h2>
                    <p className="text-lg text-gray-700">{selectedPTP.project_name}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg mb-6">
                    <div><strong>Date:</strong> {selectedPTP.date}</div>
                    <div><strong>Shift:</strong> {selectedPTP.shift?.charAt(0).toUpperCase() + selectedPTP.shift?.slice(1)} Shift</div>
                    <div><strong>Location:</strong> {selectedPTP.location}</div>
                    <div><strong>Supervisor:</strong> {selectedPTP.supervisor}</div>
                  </div>

                  {selectedPTP.tasks_planned?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">📋 Tasks Planned</h3>
                      {selectedPTP.tasks_planned.map((task, i) => (
                        <div key={i} className="p-2 bg-blue-50 rounded mb-2 border-l-3 border-blue-500">
                          {i + 1}. {task}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedPTP.hazards?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">⚠️ Identified Hazards & Controls</h3>
                      {selectedPTP.hazards.map((hazard, i) => (
                        <div key={i} className={`p-3 rounded-lg mb-2 border-l-4 ${getHazardLevelColor(hazard.level)}`}>
                          <p className="font-bold">{hazard.hazard}</p>
                          <p className="text-sm"><strong>Controls:</strong> {hazard.controls?.join(', ')}</p>
                          <p className="text-sm"><strong>PPE Required:</strong> {hazard.ppe_required?.join(', ')}</p>
                          <p className="text-sm"><strong>Risk Level:</strong> {hazard.level?.toUpperCase()}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-blue-600 border-b pb-2 mb-3">👷 Crew Sign-Off Sheet</h3>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border p-2 text-left">Name</th>
                          <th className="border p-2 text-left">Role</th>
                          <th className="border p-2 text-left">Signature</th>
                          <th className="border p-2 text-left">Date/Time</th>
                          <th className="border p-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPTP.crew?.map((member, i) => (
                          <tr key={i}>
                            <td className="border p-2">{member.name}</td>
                            <td className="border p-2">{member.role}</td>
                            <td className="border p-2 h-12">{member.signed ? '✓ Signed' : ''}</td>
                            <td className="border p-2">{member.signed_at ? new Date(member.signed_at).toLocaleString() : ''}</td>
                            <td className="border p-2">
                              <Badge className={member.signed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                {member.signed ? 'SIGNED' : 'PENDING'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                        {/* Empty rows for additional signatures */}
                        {[1, 2, 3].map(i => (
                          <tr key={`empty-${i}`}>
                            <td className="border p-2 h-10"></td>
                            <td className="border p-2"></td>
                            <td className="border p-2"></td>
                            <td className="border p-2"></td>
                            <td className="border p-2"></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(selectedPTP.emergency_contact || selectedPTP.emergency_phone || selectedPTP.nearest_hospital) && (
                    <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg mb-6">
                      <h3 className="text-red-600 font-bold mb-2">🚨 EMERGENCY INFORMATION</h3>
                      {selectedPTP.emergency_contact && <p><strong>Contact:</strong> {selectedPTP.emergency_contact}</p>}
                      {selectedPTP.emergency_phone && <p><strong>Phone:</strong> {selectedPTP.emergency_phone}</p>}
                      {selectedPTP.nearest_hospital && <p><strong>Nearest Hospital:</strong> {selectedPTP.nearest_hospital}</p>}
                    </div>
                  )}

                  <div className="border-t-2 pt-4 text-center text-gray-500 text-sm">
                    <p className="mb-4">
                      <strong>Supervisor Approval:</strong> _________________ 
                      <strong className="ml-8">Date:</strong> _________________
                    </p>
                    <p>Generated from WSafe AI Safety Platform</p>
                    <p>© {new Date().getFullYear()} WorkSafeAgent LLC | www.wsafeai.com</p>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyPTPPage;
